import { Crc32AuthAuthenticator } from './crc32AuthAuthenticator'
import {
  Crc32AuthKeyProvider,
  FixedCrc32AuthKeyProvider,
} from './crc32AuthKeyProvider'
import { crc8 } from './crcAlgorithms'
import {
  Crc32AuthCommand,
  FRAME_HEADER,
  FRAME_FOOTER,
  TIMESTAMP_BYTE_LENGTH,
  AUTH_CODE_BYTE_LENGTH,
  TIMESTAMP_MASK,
} from './crc32AuthTypes'

type Options = {
  keyProvider?: Crc32AuthKeyProvider
  authenticator?: Crc32AuthAuthenticator
  timestampProvider?: () => number
}

export class Crc32AuthPacketBuilder {
  private readonly keyProvider: Crc32AuthKeyProvider
  private readonly authenticator: Crc32AuthAuthenticator
  private readonly timestampProvider: () => number
  private lastTimestamp: number | null = null

  constructor(options: Options = {}) {
    this.keyProvider = options.keyProvider ?? new FixedCrc32AuthKeyProvider()
    this.authenticator = options.authenticator ?? new Crc32AuthAuthenticator()
    this.timestampProvider = options.timestampProvider ?? (() => Date.now())
  }

  buildRentalPacket(): Uint8Array {
    return this.buildPacket(Crc32AuthCommand.rental)
  }

  buildReturnPacket(): Uint8Array {
    return this.buildPacket(Crc32AuthCommand.returnExec)
  }

  buildPacket(commandCode: number, data: number[] = []): Uint8Array {
    const commandAndData = [commandCode, ...data]
    const lengthFieldValue =
      commandAndData.length + TIMESTAMP_BYTE_LENGTH + AUTH_CODE_BYTE_LENGTH
    const timestamp = this.nextTimestamp()
    const packetBeforeAuth = [
      FRAME_HEADER,
      ...toLittleEndianBytes(lengthFieldValue, 2),
      ...commandAndData,
      ...toLittleEndianBytes(timestamp, TIMESTAMP_BYTE_LENGTH),
    ]
    const authCode = this.authenticator.generate({
      key: this.keyProvider.key,
      data: packetBeforeAuth,
    })
    const packetBeforeCrc = [...packetBeforeAuth, ...authCode]
    const crc = crc8(packetBeforeCrc)
    return Uint8Array.from([...packetBeforeCrc, crc, FRAME_FOOTER])
  }

  private nextTimestamp(): number {
    let timestamp = maskTimestamp(this.timestampProvider())
    const last = this.lastTimestamp
    if (last !== null && timestamp <= last) {
      timestamp = maskTimestamp(last + 1)
    }
    this.lastTimestamp = timestamp
    return timestamp
  }
}

function maskTimestamp(value: number): number {
  // value & 0xffffffffff 相当（40bit。`&` は32bitに丸めるため modulo を使う）
  return value % (TIMESTAMP_MASK + 1)
}

function toLittleEndianBytes(value: number, length: number): number[] {
  const bytes: number[] = []
  for (let i = 0; i < length; i++) {
    bytes.push(Math.floor(value / 2 ** (i * 8)) % 256)
  }
  return bytes
}
